import { useEffect } from 'react'
import { UseFormReturn } from 'react-hook-form'
import clsx from 'clsx'
import { Table } from '@tanstack/react-table'
import { format, subDays } from 'date-fns'
import { Input } from '@/ui/Input'
import { InputFormValues } from '@/modules/CombinedLogs/types'
import { getNameInitials } from '@/helpers/getNameInitials'
import { useGetUserInfo, useNightShiftInfo } from '@/modules/Auth'
import { useGetReference } from '@/modules/CombinedLogs/api/useGetReference'
import { Log } from '@/types/logs'
import { checkIsLogUrgent } from '@/modules/CombinedLogs/helpers/checkIsLogUrgent'
import { useCombinedLogsTableFormStateStore } from '@/modules/CombinedLogs/store'
import styles from '../../CombinedLogs.module.scss'
import { TableFormRouteInput } from './FormInputs/TableFormRouteInput'
import { TableFormDelayInput } from './FormInputs/TableFormDelayInput'
import { TableFormCodeInput } from './FormInputs/TableFormCodeInput'
import { TableFormLocationInput } from './FormInputs/TableFormLocationInput'
import { TableFormTripInput } from './FormInputs/TableFormTripInput'
import { TableFormDescriptionInputs } from './FormInputs/TableFormDescriptionInput'
import { TableFormLogActionInput } from './FormInputs/TableFormLogActionInput'
import { TableFormOriginalTripInput } from './FormInputs/TableFormOriginalTripInput'
import { TableFormDutyInput } from './FormInputs/TableFormDutyInput'
import { useAuthStore } from '@/store/auth-store'
import { Role } from '@/types/auth'
import { TimeInputContainer } from './FormInputs/TimeInput/TimeInputContainer'
import { COMBINED_LOGS_COLUMNS } from '@/modules/CombinedLogs/constant'

export const TableForm = ({
    form,
    isActive,
    table,
}: {
    form: UseFormReturn<InputFormValues>
    isActive: boolean
    table: Table<Log>
}) => {
    const { data: userInfo } = useGetUserInfo()
    const { data: reference } = useGetReference()
    const { isNightShift, isAfterMidnight } = useNightShiftInfo()
    const { reason, isBulkCurtailment, setNightShiftDate } = useCombinedLogsTableFormStateStore()
    const { role } = useAuthStore()

    const generateDayOptions = () => {
        if (!isNightShift && !isAfterMidnight()) return []

        try {
            const today = new Date()
            const yesterday = subDays(today, 1)

            const formatter = new Intl.DateTimeFormat('en-GB', {
                weekday: 'long',
            })

            return [
                {
                    id: '1',
                    label: formatter.format(yesterday),
                    value: yesterday
                        .toLocaleDateString('en-CA', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                        })
                        .split('T')[0],
                },
                {
                    id: '2',
                    label: formatter.format(today),
                    value: today
                        .toLocaleDateString('en-CA', {
                            year: 'numeric',
                            month: '2-digit',
                            day: '2-digit',
                        })
                        .split('T')[0],
                },
            ]
        } catch (error) {
            console.error('Error parsing startShiftDate:', error)
            return []
        }
    }
    const isExtraMileage = form.watch('code') === 'E'
    const bulkCurtailmentRunTo = form.watch('bulkCurtailmentRunTo')
    const bulkCurtailmentDutyTo = form.watch('bulkCurtailmentDutyTo')
    const bulkCurtailmentFleetTo = form.watch('bulkCurtailmentFleetTo')
    const routes = form.watch('routes')
    const isZeroMileage = form.watch('isZeroMileage')

    const dayOptions = generateDayOptions()

    useEffect(() => {
        if (isActive) {
            form.setFocus('code')
            form.setValue('author', getNameInitials(userInfo?.lastSessionDetails.authorName))
            form.setValue('timeStamp', format(new Date(), 'HH:mm'))
            if (isNightShift && isAfterMidnight()) {
                setNightShiftDate(dayOptions[1].value)
                form.setValue('nightShiftDate', dayOptions[1].value)
            }
        }
    }, [isActive, form, userInfo?.lastSessionDetails.authorName])

    return (
        <>
            {table.getHeaderGroups().map((headerGroup) => {
                const isUrgent = checkIsLogUrgent({ codeTypeReasonInfo: reason } as Log)
                return (
                    <tr
                        className={clsx(styles.formRow, styles.active, {
                            [styles.hidden]: !isActive,
                            [styles.urgentRow]: isUrgent,
                        })}
                        key={headerGroup.id}
                        data-urgent={isUrgent}
                    >
                        <td className={clsx(styles.formCell, styles.alignCenterFormCell)} key={'code'}>
                            <TableFormCodeInput name="code" form={form} />
                        </td>
                        <td className={clsx(styles.formCell, styles.alignCenterFormCell)} key={'reference'}>
                            <span className={styles.reference}>{reference}</span>
                            {isExtraMileage && (
                                <div>
                                    <p className={styles.originalTripFormTitle}>Original trip:</p>
                                </div>
                            )}
                        </td>

                        <td className={clsx(styles.formCell, styles.alignCenterFormCell)} key={'routes'}>
                            {isBulkCurtailment && routes ? (
                                <span className={styles.reference}>{routes}</span>
                            ) : (
                                <>
                                    <TableFormRouteInput name="routes" control={form.control} form={form} />
                                    {isExtraMileage && (
                                        <div className={styles.originalRoutesFormContainer}>
                                            <TableFormRouteInput
                                                name="originalRoutes"
                                                control={form.control}
                                                form={form}
                                            />
                                        </div>
                                    )}
                                </>
                            )}
                        </td>
                        <td className={clsx(styles.formCell, styles.alignCenterFormCell)} key={'trip'}>
                            <TableFormTripInput form={form} />
                            {isExtraMileage && (
                                <div className={styles.originalRoutesFormContainer}>
                                    <TableFormOriginalTripInput form={form} />
                                </div>
                            )}
                            {isBulkCurtailment &&
                                (bulkCurtailmentRunTo || bulkCurtailmentDutyTo || bulkCurtailmentFleetTo) && (
                                    <div className={styles.formCell}>
                                        <span>to</span>
                                    </div>
                                )}
                        </td>
                        <td className={styles.formCell} key={'run'}>
                            <Input
                                {...form.register('run')}
                                variant="table"
                                type="text"
                                transparent
                                fullWidth
                                placeholder={'Run'}
                            />
                            {isExtraMileage && (
                                <div className={styles.originalRoutesFormContainer}>
                                    <Input
                                        {...form.register('originalRun')}
                                        variant="table"
                                        type="text"
                                        transparent
                                        fullWidth
                                        placeholder={'Run'}
                                    />
                                </div>
                            )}
                            {isBulkCurtailment && bulkCurtailmentRunTo && (
                                <div className={clsx(styles.formCell, styles.bulkCurtailmentValue)}>
                                    <span>{bulkCurtailmentRunTo}</span>
                                </div>
                            )}
                        </td>
                        <td className={styles.formCell} key={'duty'}>
                            <TableFormDutyInput form={form} name="duty" />
                            {isExtraMileage && (
                                <div className={styles.originalRoutesFormContainer}>
                                    <TableFormDutyInput form={form} name="originalDuty" />
                                </div>
                            )}
                            {isBulkCurtailment && bulkCurtailmentDutyTo && (
                                <div className={clsx(styles.formCell, styles.bulkCurtailmentValue)}>
                                    <span>{bulkCurtailmentDutyTo}</span>
                                </div>
                            )}
                        </td>
                        <td className={styles.formCell} key={'fleet'}>
                            <Input
                                {...form.register('fleet')}
                                variant="table"
                                type="text"
                                transparent
                                fullWidth
                                placeholder={'Fleet'}
                            />
                            {isExtraMileage && (
                                <div className={styles.originalRoutesFormContainer}>
                                    <Input
                                        {...form.register('originalFleet')}
                                        variant="table"
                                        type="text"
                                        transparent
                                        fullWidth
                                        placeholder={'Fleet'}
                                    />
                                </div>
                            )}
                            {isBulkCurtailment && bulkCurtailmentFleetTo && (
                                <div className={clsx(styles.formCell, styles.bulkCurtailmentValue)}>
                                    <span>{bulkCurtailmentFleetTo}</span>
                                </div>
                            )}
                        </td>
                        <td className={styles.formCell} style={{ textAlign: 'start' }} key={'locationFrom'}>
                            <TableFormLocationInput form={form} />
                        </td>
                        <td className={styles.formCell} style={{ textAlign: 'start' }} key={'description'}>
                            <TableFormDescriptionInputs
                                form={form}
                                table={table}
                                isBulkCurtailment={isBulkCurtailment}
                            />
                        </td>
                        {role !== Role.MileageClerk && (
                            <td className={styles.formCell} key={'delay'}>
                                <TableFormDelayInput name="delay" control={form.control} />
                            </td>
                        )}
                        <td className={styles.formCell} key={'mileage'}>
                            <Input
                                {...(isZeroMileage === 'true' ? { value: '0.00' } : form.register('mileage'))}
                                variant="table"
                                type="number"
                                transparent
                                fullWidth
                                className={styles.greyInput}
                                placeholder={'Mileage'}
                                readOnly
                            />
                        </td>
                        <td className={styles.formCell} key={'author'}>
                            <Input
                                {...form.register('author')}
                                variant="table"
                                type="text"
                                transparent
                                fullWidth
                                className={styles.greyInput}
                                placeholder={'Author'}
                                readOnly
                            />
                        </td>
                        <td className={styles.formCell} key={'timeStamp'}>
                            <TimeInputContainer form={form} dayOptions={dayOptions} />
                        </td>
                        {table.getColumn(COMBINED_LOGS_COLUMNS.Unfinished)?.getIsVisible() && (
                            <td className={styles.formCell} key={'unfinished'}></td>
                        )}
                        {table.getColumn(COMBINED_LOGS_COLUMNS.LogActions)?.getIsVisible() && (
                            <td className={styles.formCell} key={'action'}>
                                <TableFormLogActionInput form={form} isRowSelected />
                            </td>
                        )}
                        {table.getColumn(COMBINED_LOGS_COLUMNS.Lock)?.getIsVisible() && (
                            <td className={styles.formCell} key={'lock'}></td>
                        )}
                        <td className={styles.formCell} key={'actions'}></td>
                    </tr>
                )
            })}
        </>
    )
}
